import React from 'react';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Radio,
  Select,
  SelectProps,
  makeStyles
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import {useTranslation} from 'react-i18next';
import {
  INPUT_BORDER_COLOR,
  INPUT_LABEL_COLOR,
  MENU_ITEM_BORDER_BOTTOM_COLOR
} from 'src/style/variable';

interface ISelectOptionItem {
  label: string;
  value: string | number;
  // eslint-disable-next-line camelcase
  secondary_label?: string;
  option_group?: string;
}
interface ISelectInputField<T extends ISelectOptionItem>
  extends Omit<SelectProps, 'error'> {
  label?: string;
  error?: Record<string, any>;
  itemList: T[];
  itemsHeaderLabel?: string;
  onClear?: () => void;
  renderMenuItem?: (item: T) => JSX.Element;
  noItemLabel: string;
  showRadio?: boolean;
  showSecondaryLabelInValue?: boolean;
  wrapOptionLabel?: boolean;
  isSelectedOption?: (item: T, value: T['value']) => boolean;
}

const styles = makeStyles(() => ({
  selectInput: {
    '&.no-value': {
      '& .MuiSelect-select': {
        opacity: 0.5
      },
      '& .MuiSelect-icon': {
        opacity: 0.5
      }
    },
    '& .MuiSelect-select': {
      display: 'block'
    },
    '& .MuiSelect-select > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: INPUT_BORDER_COLOR
    }
  },
  selectMenuWrapper: {
    maxWidth: '32rem',
    minWidth: '28rem !important',
    '& .MuiMenuItem-root': {
      borderBottom: `1px solid ${MENU_ITEM_BORDER_BOTTOM_COLOR}`
    }
  },
  selectMenuItem: {
    fontSize: '1.2rem',
    paddingBlock: '0.75rem'
  },
  listItem: (props: any) => ({
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      marginTop: '5px',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    '& .MuiListItemText-primary': {
      fontSize: '1.2rem',
      overflow: 'hidden',
      width: '100%',
      textOverflow: 'ellipsis',
      ...(props.wrapOptionLabel
        ? {
            textWrap: 'auto'
          }
        : {whiteSpace: 'nowrap'})
    }
  }),
  menuItem: {
    paddingBlock: 0
  },
  formControl: {
    paddingBottom: 0,
    marginBottom: 0,
    '& .MuiFormLabel-asterisk': {
      color: 'inherit'
    }
  },
  helperTextWrapper: {
    marginLeft: 0
  },
  optionGroupLabel: {
    position: 'static',
    lineHeight: '1.5em',
    borderBottom: '1px solid #eeeeee',
    fontSize: '1.1rem',
    paddingBlock: '0.25rem'
  },
  menuItemWithOptionGroup: {
    paddingLeft: '2rem'
  }
}));

const SelectInputField = <T extends ISelectOptionItem>(
  props: ISelectInputField<T>
): JSX.Element => {
  const {
    error,
    // TODO: RENAME IT TO optionsList
    itemList = [],
    // TODO: RENAME IT TO optionsHeaderLabel
    itemsHeaderLabel,
    label,
    renderMenuItem,
    onClear,
    className,
    noItemLabel,
    wrapOptionLabel = false,
    showRadio = false,
    showSecondaryLabelInValue = true,
    isSelectedOption = (item: T, v: T['value']) => item.value === v,
    ...rest
  } = props;
  const {placeholder, value} = rest;
  const classes = styles({wrapOptionLabel});
  const {t} = useTranslation();

  const selectedOption = itemList.find(item =>
    isSelectedOption(item, value as T['value'])
  );
  const renderValue = () => {
    if (selectedOption) {
      let valueLabel = '';
      if (selectedOption.label) {
        valueLabel += selectedOption.label;
        if (selectedOption.secondary_label && showSecondaryLabelInValue) {
          valueLabel += ` (${selectedOption.secondary_label})`;
        }
      }
      return valueLabel;
    }
    return placeholder;
  };

  const extraDefaultProps: SelectProps = {};
  extraDefaultProps.renderValue = renderValue;

  const renderItemList = (optionList: T[]) => {
    const hasItemWithOptionGroup = optionList.some(i => i.option_group);

    if (!hasItemWithOptionGroup) {
      return optionList.map(item => {
        if (renderMenuItem) {
          return renderMenuItem(item);
        }
        return (
          <MenuItem
            key={item.value}
            value={item.value}
            className={classes.menuItem}>
            <ListItemText
              className={classes.listItem}
              primary={item.label}
              secondary={item?.secondary_label ?? ''}
            />
            {showRadio && <Radio checked={value === item.value} />}
          </MenuItem>
        );
      });
    }
    // Group Items based on option_group
    const optionsGroup = new Map<any, T[]>();
    optionList.forEach(i => {
      if (optionsGroup.has(i.option_group)) {
        optionsGroup.set(i.option_group, [
          ...optionsGroup.get(i.option_group),
          i
        ]);
      } else {
        optionsGroup.set(i.option_group, [i]);
      }
    });

    const groupedOptionsJsx = [];
    Array.from(optionsGroup).forEach(([key, optionItems]) => {
      if (key) {
        groupedOptionsJsx.push(
          <ListSubheader key={key} className={classes.optionGroupLabel}>
            {key}
          </ListSubheader>
        );
      }
      optionItems.map(item => {
        if (renderMenuItem) {
          groupedOptionsJsx.push(renderMenuItem(item));
        }
        groupedOptionsJsx.push(
          <MenuItem
            key={`${key}-${item.value}`}
            value={item.value}
            className={`${classes.menuItem} ${classes.menuItemWithOptionGroup}`}>
            <ListItemText
              className={classes.listItem}
              primary={item.label}
              secondary={item?.secondary_label ?? ''}
            />
            {showRadio && <Radio checked={value === item.value} />}
          </MenuItem>
        );
      });
    });

    return groupedOptionsJsx;
  };

  return (
    <FormControl
      error={!!error}
      fullWidth={true}
      variant="outlined"
      className={`pc-form-field-select ${classes.formControl}`}
      required={rest?.required}>
      {label && (
        <InputLabel
          style={{
            position: 'static',
            transform: 'none',
            color: INPUT_LABEL_COLOR,
            fontSize: '1.1rem',
            marginBottom: '0.5rem'
          }}
          shrink>
          {label}
        </InputLabel>
      )}
      <Select
        data-test-id="pcl-practices-patientState"
        fullWidth={true}
        variant="outlined"
        margin="dense"
        IconComponent={KeyboardArrowDownIcon}
        displayEmpty
        MenuProps={{
          PaperProps: {
            className: classes.selectMenuWrapper
          }
        }}
        className={`${classes.selectInput} ${
          !selectedOption ? 'no-value' : ''
        } ${className}`}
        {...extraDefaultProps}
        {...rest}
        // Assigning empty string as default value. Because below we have used MenuItem with empty string as value to display Placeholder
        value={selectedOption ? value : ''}>
        {onClear && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.5rem 1rem',
              gap: '1rem'
            }}>
            <p
              style={{
                fontSize: '1.5rem',
                fontStyle: 'regular'
              }}>
              {itemsHeaderLabel ?? label}
            </p>
            {onClear && (
              <Button onClick={onClear}>{t('COMMON.CLEAR_ALL')}</Button>
            )}
          </div>
        )}
        <Divider />
        <MenuItem value={''} style={{display: 'none'}}>
          {placeholder}
        </MenuItem>
        {itemList.length ? (
          renderItemList(itemList)
        ) : (
          <MenuItem disabled>{noItemLabel ?? 'No Items'}</MenuItem>
        )}
      </Select>
      {error && (
        <FormHelperText className={classes.helperTextWrapper}>
          {t(error.message)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SelectInputField;
